import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SafeIcon from '../../common/SafeIcon';

const pad = (value) => String(value).padStart(2, '0');
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Mo','Tu','We','Th','Fr','Sa','Su'];

const toLocalDateValue = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
);

const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  }
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const au = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (au) {
    const date = new Date(Number(au[3]), Number(au[2]) - 1, Number(au[1]), 12);
    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== Number(au[3]) ||
      date.getMonth() !== Number(au[2]) - 1 ||
      date.getDate() !== Number(au[1])
    ) return null;
    return date;
  }
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
};

const formatDisplayDate = (value) => {
  const date = parseLocalDate(value);
  return date ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}` : '';
};

const addMonthsClamped = (base, months) => {
  const originalDay = base.getDate();
  const target = new Date(base.getFullYear(), base.getMonth() + months, 1, 12);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0, 12).getDate();
  target.setDate(Math.min(originalDay, lastDay));
  return target;
};

const formatContextDate = (date) => date.toLocaleDateString('en-AU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const isSameDay = (a, b) => (
  Boolean(a && b) &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()
);

const buildCalendarDays = (viewDate) => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1, 12);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

const SmartDateInput = ({
  name,
  value = '',
  onChange,
  className = '',
  required = false,
  disabled = false,
  min,
  max,
  baseDate,
  baseLabel = '',
  quickOptions,
  id,
  'aria-label': ariaLabel,
  ...inputProps
}) => {
  const containerRef = useRef(null);
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(() => parseLocalDate(value));
  const [viewDate, setViewDate] = useState(() => parseLocalDate(value) || new Date());
  const [manualText, setManualText] = useState(() => formatDisplayDate(value));
  const [jumpText, setJumpText] = useState('');
  const [panelStyle, setPanelStyle] = useState({});

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  }, []);

  const minDate = useMemo(() => parseLocalDate(min), [min]);
  const maxDate = useMemo(() => parseLocalDate(max), [max]);
  const contextualBase = useMemo(() => parseLocalDate(baseDate) || today, [baseDate, today]);
  const selectedDate = useMemo(() => parseLocalDate(value), [value]);
  const calendarDays = useMemo(() => buildCalendarDays(viewDate), [viewDate]);

  const yearOptions = useMemo(() => {
    const minimumYear = minDate?.getFullYear() ?? 1900;
    const maximumYear = maxDate?.getFullYear() ?? (today.getFullYear() + 50);
    const years = [];
    for (let year = minimumYear; year <= maximumYear; year += 1) years.push(year);
    return years;
  }, [minDate, maxDate, today]);

  const options = useMemo(() => quickOptions || [
    { id: 'today', label: 'Today', getDate: () => today },
    { id: '30d', label: '+30 Days', getDate: (base) => { const date = new Date(base); date.setDate(date.getDate() + 30); return date; } },
    { id: '6m', label: '+6 Months', getDate: (base) => addMonthsClamped(base, 6) },
    { id: '12m', label: '+12 Months', getDate: (base) => addMonthsClamped(base, 12) }
  ], [quickOptions, today]);

  const isAllowed = useCallback((date) => {
    if (!date) return false;
    const stamp = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).getTime();
    if (minDate && stamp < minDate.getTime()) return false;
    if (maxDate && stamp > maxDate.getTime()) return false;
    return true;
  }, [minDate, maxDate]);

  const emitValue = useCallback((nextValue) => {
    if (typeof onChange !== 'function') return;
    const target = { name, value: nextValue, type: 'date' };
    onChange({ target, currentTarget: target });
  }, [name, onChange]);

  const updatePanelPosition = useCallback(() => {
    if (!anchorRef.current || typeof window === 'undefined') return;
    const rect = anchorRef.current.getBoundingClientRect();
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const margin = 12;
    const gap = 8;

    if (viewportWidth < 640) {
      setPanelStyle({
        left: margin,
        right: margin,
        bottom: margin,
        top: margin,
        width: 'auto',
        maxHeight: 'none'
      });
      return;
    }

    const width = Math.min(620, viewportWidth - (margin * 2));
    const left = Math.max(margin, Math.min(rect.left, viewportWidth - width - margin));
    const availableBelow = Math.max(0, viewportHeight - rect.bottom - gap - margin);
    const availableAbove = Math.max(0, rect.top - gap - margin);
    const comfortableHeight = 430;

    if (availableBelow >= comfortableHeight || availableBelow >= availableAbove) {
      const top = Math.max(margin, rect.bottom + gap);
      setPanelStyle({
        left,
        top,
        bottom: 'auto',
        width,
        maxHeight: Math.max(160, viewportHeight - top - margin)
      });
      return;
    }

    const bottom = Math.max(margin, viewportHeight - rect.top + gap);
    setPanelStyle({
      left,
      top: 'auto',
      bottom,
      width,
      maxHeight: Math.max(160, viewportHeight - bottom - margin)
    });
  }, []);

  const openPicker = useCallback(() => {
    if (disabled) return;
    const seed = selectedDate || contextualBase || today;
    setDraftDate(selectedDate);
    setViewDate(seed);
    setJumpText('');
    setIsOpen(true);
  }, [disabled, selectedDate, contextualBase, today]);

  useEffect(() => {
    setManualText(formatDisplayDate(value));
    if (!isOpen) {
      const parsed = parseLocalDate(value);
      if (parsed) setViewDate(parsed);
      setDraftDate(parsed);
    }
  }, [value, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePanelPosition();
    const handlePointerDown = (event) => {
      const panel = document.querySelector('[data-mpp-smart-date-panel="true"]');
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        panel &&
        !panel.contains(event.target)
      ) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const reposition = () => updatePanelPosition();

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    window.visualViewport?.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('scroll', reposition);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      window.visualViewport?.removeEventListener('resize', reposition);
      window.visualViewport?.removeEventListener('scroll', reposition);
    };
  }, [isOpen, updatePanelPosition]);

  const commitManualText = () => {
    const trimmed = manualText.trim();
    if (!trimmed) {
      if (!required) emitValue('');
      else setManualText(formatDisplayDate(value));
      return;
    }
    const parsed = parseLocalDate(trimmed);
    if (parsed && isAllowed(parsed)) {
      emitValue(toLocalDateValue(parsed));
      setDraftDate(parsed);
      setViewDate(parsed);
      setManualText(formatDisplayDate(parsed));
    } else {
      setManualText(formatDisplayDate(value));
    }
  };

  const selectDate = (date) => {
    if (!isAllowed(date)) return;
    setDraftDate(date);
    setViewDate(new Date(date.getFullYear(), date.getMonth(), 1, 12));
  };

  const applyDraft = () => {
    if (!draftDate || !isAllowed(draftDate)) return;
    emitValue(toLocalDateValue(draftDate));
    setManualText(formatDisplayDate(draftDate));
    setIsOpen(false);
  };

  const applyQuickDate = (option) => {
    const base = option.id === 'today' ? today : contextualBase;
    const nextDate = option.getDate(base);
    if (!isAllowed(nextDate)) return;
    emitValue(toLocalDateValue(nextDate));
    setManualText(formatDisplayDate(nextDate));
    setDraftDate(nextDate);
    setViewDate(nextDate);
    setIsOpen(false);
  };

  const jumpToDate = () => {
    const parsed = parseLocalDate(jumpText);
    if (!parsed || !isAllowed(parsed)) return;
    setDraftDate(parsed);
    setViewDate(parsed);
    setJumpText(formatDisplayDate(parsed));
  };

  const moveMonth = (offset) => {
    setViewDate(current => new Date(current.getFullYear(), current.getMonth() + offset, 1, 12));
  };

  const picker = isOpen ? createPortal(
    <>
      <button
        type="button"
        className="fixed inset-0 z-[998] hidden bg-slate-950/20 backdrop-blur-[1px] max-sm:block"
        aria-label="Close date picker"
        onClick={() => setIsOpen(false)}
      />
      <div
        data-mpp-smart-date-panel="true"
        role="dialog"
        aria-modal="false"
        aria-label="Smart date picker"
        className="fixed z-[999] flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        style={panelStyle}
      >
        <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-900">Select date</div>
              <div className="mt-0.5 text-[11px] leading-4 text-slate-500">
                {baseDate
                  ? `Quick offsets use ${baseLabel || 'the related date'}: ${formatContextDate(contextualBase)}`
                  : 'Quick offsets use today as the starting point.'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close date picker"
            >
              <SafeIcon icon="X" />
            </button>
          </div>
        </div>

        <div
          className="grid min-h-0 flex-1 gap-0 overflow-y-auto sm:grid-cols-[minmax(0,1fr)_190px]"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
        >
          <div className="p-4 sm:p-5">
            <div className="mb-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => moveMonth(-1)}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                aria-label="Previous month"
              >
                <SafeIcon icon="ChevronLeft" />
              </button>

              <select
                value={viewDate.getMonth()}
                onChange={(event) => setViewDate(new Date(viewDate.getFullYear(), Number(event.target.value), 1, 12))}
                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Select month"
              >
                {MONTHS.map((month, index) => <option key={month} value={index}>{month}</option>)}
              </select>

              <select
                value={viewDate.getFullYear()}
                onChange={(event) => setViewDate(new Date(Number(event.target.value), viewDate.getMonth(), 1, 12))}
                className="w-[92px] rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Select year"
              >
                {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
              </select>

              <button
                type="button"
                onClick={() => moveMonth(1)}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                aria-label="Next month"
              >
                <SafeIcon icon="ChevronRight" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map(day => (
                <div key={day} className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">{day}</div>
              ))}
              {calendarDays.map((date) => {
                const inMonth = date.getMonth() === viewDate.getMonth();
                const selected = isSameDay(date, draftDate || selectedDate);
                const todayDate = isSameDay(date, today);
                const allowed = isAllowed(date);
                return (
                  <button
                    key={toLocalDateValue(date)}
                    type="button"
                    disabled={!allowed}
                    onClick={() => selectDate(date)}
                    className={[
                      'relative flex aspect-square min-h-[36px] items-center justify-center rounded-lg text-xs font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-amber-500 sm:min-h-[40px]',
                      selected
                        ? 'bg-amber-500 text-white shadow-sm'
                        : inMonth
                          ? 'text-slate-700 hover:bg-amber-50 hover:text-amber-800'
                          : 'text-slate-300 hover:bg-slate-50',
                      todayDate && !selected ? 'ring-1 ring-inset ring-amber-400 text-amber-700' : '',
                      !allowed ? 'cursor-not-allowed opacity-30 hover:bg-transparent' : ''
                    ].join(' ')}
                    aria-pressed={selected}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-100 bg-slate-50/70 p-4 sm:border-l sm:border-t-0 sm:p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Quick Select</div>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-1">
              {options.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => applyQuickDate(option)}
                  className="min-h-[40px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <span className="inline-flex items-center gap-2">
                    <SafeIcon icon="Calendar" className="text-sm text-amber-600" />
                    {option.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="my-4 border-t border-slate-200" />

            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Go to Date</div>
            <div className="mt-2 flex gap-2 sm:block">
              <input
                type="text"
                value={jumpText}
                onChange={(event) => setJumpText(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); jumpToDate(); } }}
                placeholder="dd/mm/yyyy"
                inputMode="numeric"
                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 sm:w-full"
              />
              <button
                type="button"
                onClick={jumpToDate}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 sm:mt-2 sm:w-full"
              >
                Go
              </button>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-white px-4 py-3 sm:px-5">
          <button
            type="button"
            onClick={() => {
              if (!required) emitValue('');
              setDraftDate(null);
              setManualText(required ? formatDisplayDate(value) : '');
              if (!required) setIsOpen(false);
            }}
            className="rounded-lg px-2 py-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-40"
            disabled={required}
          >
            Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyDraft}
              disabled={!draftDate || !isAllowed(draftDate)}
              className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <div ref={containerRef} className="relative">
      <div ref={anchorRef} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <div className="relative min-w-0">
          <input
            {...inputProps}
            id={id}
            type="text"
            name={name}
            value={manualText}
            onChange={(event) => setManualText(event.target.value)}
            onBlur={commitManualText}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                openPicker();
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                commitManualText();
                openPicker();
              }
            }}
            className={`${className} pr-10`}
            required={required}
            disabled={disabled}
            inputMode="numeric"
            placeholder={inputProps.placeholder || 'dd/mm/yyyy'}
            autoComplete="off"
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
          />
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-500 transition-colors hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Open calendar"
            tabIndex={-1}
          >
            <SafeIcon icon="Calendar" />
          </button>
        </div>

        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50 sm:px-3"
          aria-expanded={isOpen}
          aria-label="Open smart date picker"
          title="Smart date picker"
        >
          <SafeIcon icon="Calendar" className="text-sm text-amber-600" />
          <span className="hidden sm:inline">Quick</span>
          <SafeIcon icon="ChevronDown" className={`text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {picker}
    </div>
  );
};

export default SmartDateInput;